import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Button,
  Box,
  TextField,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useCart } from '../context/CartContext';
import { loadStripe } from '@stripe/stripe-js';
import { createCartCheckoutSession } from '../services/stripe';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY!);

interface CartProps {
  open: boolean;
  onClose: () => void;
}

interface CartItem {
  id: string;
  quantity: number;
  name: string;
  price: number;
  image: string;
}

const Cart: React.FC<CartProps> = ({ open, onClose }) => {
  const { items, removeFromCart, updateQuantity, totalPrice, clearCart } = useCart();
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleQuantityChange = (id: string, value: string) => {
    const quantity = parseInt(value, 10);
    if (!isNaN(quantity) && quantity > 0) {
      updateQuantity(id, quantity);
    }
  };

  const handleCheckout = async () => {
    try {
      setIsCheckingOut(true);
      setError(null);
      console.log('Creating checkout session...');
      
      // Convert cart items to include required properties
      const checkoutItems = items.map((item: CartItem) => {
        return {
          id: item.id,
          quantity: item.quantity,
          name: item.name,
          price: item.price,
          image: item.image
        };
      });

      const sessionId: string = await createCartCheckoutSession(checkoutItems);
      console.log('Received session ID:', sessionId);

      console.log('Redirecting to Stripe checkout...');
      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Stripe not initialized');
      }

      const { error } = await stripe.redirectToCheckout({
        sessionId,
      });

      if (error) {
        throw error;
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err instanceof Error ? err.message : 'Failed to create checkout session');
    } finally {
      setIsCheckingOut(false);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 350, p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Shopping Cart
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {items.length === 0 ? (
          <Typography variant="body1">Your cart is empty</Typography>
        ) : (
          <>
            <List>
              {items.map((item: CartItem) => (
                <React.Fragment key={item.id}>
                  <ListItem>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <img
                        src={item.image}
                        alt={item.name}
                        style={{ width: 50, height: 50, objectFit: 'cover', marginRight: 16 }}
                      />
                      <Box sx={{ flexGrow: 1 }}>
                        <ListItemText
                          primary={item.name}
                          secondary={`$${((item.price || 0) * item.quantity).toFixed(2)}`}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                          <TextField
                            type="number"
                            size="small"
                            value={item.quantity}
                            onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                            inputProps={{ min: 1 }}
                            sx={{ width: 60 }}
                            disabled={isCheckingOut}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              aria-label="delete"
                              onClick={() => removeFromCart(item.id)}
                              disabled={isCheckingOut}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </Box>
                      </Box>
                    </Box>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Total: ${totalPrice.toFixed(2)}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                sx={{ mt: 2 }}
                onClick={handleCheckout}
                disabled={isCheckingOut}
              >
                {isCheckingOut ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Proceed to Checkout'
                )}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                fullWidth
                sx={{ mt: 1 }}
                onClick={clearCart}
                disabled={isCheckingOut}
              >
                Clear Cart
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default Cart;
